import React from 'react';
import { Link } from 'react-router-dom';
import {
  Star, GraduationCap, ArrowRight, Sparkles, CheckCircle2,
  X, AlertTriangle, Check, MessageCircle, Monitor,
  User, Calendar, Euro, Ban, Clock, BookOpen, Laptop,
  Target, HandHeart, TrendingUp, Video, Wifi, Home as HomeIcon, Clock3, Cloud, PenTool,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Icon from '../components/Icon.jsx';
import Reveal from '../components/Reveal.jsx';
import Typewriter from '../components/Typewriter.jsx';
import CountUpStat from '../components/CountUpStat.jsx';
import LiveAvailability from '../components/LiveAvailability.jsx';
import TiltCard from '../components/TiltCard.jsx';
import PriceCalculator from '../components/PriceCalculator.jsx';
import {
  approachSteps, testimonials, subjects,
  HERO_IMG, ABOUT_IMG, ONLINE_IMG, painProblems, ourSolutions,
  flyerStudentFeatures, wcColors, CONTACT, values,
} from '../mock';

const VALUE_ICONS = { Target, HandHeart, TrendingUp, Sparkles };

const ICON_MAP = { User, Calendar, Euro, Ban, Clock, BookOpen, Laptop, Monitor };

function WatercolorFeature({ item, delay = 0 }) {
  const c = wcColors[item.color] || wcColors.blue;
  const IconComp = ICON_MAP[item.icon] || User;
  return (
    <Reveal variant="left" delay={delay}>
      <div className="flex items-start gap-4">
        <div className={`flex-shrink-0 h-14 w-14 watercolor-icon ${c.bg} ring-4 ${c.ring}`}>
          <IconComp className={`h-6 w-6 ${c.text}`} />
        </div>
        <div className="pt-1">
          <h4 className={`font-extrabold tracking-wide text-sm ${c.title}`}>{item.title}</h4>
          <p className="text-slate-700 text-sm mt-1">{item.text}</p>
        </div>
      </div>
    </Reveal>
  );
}

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="hero-bg relative overflow-hidden">
        {/* Floating decorative symbols – above content, at the hero edges so they never hide behind the image */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="hero-floaters">
          <div data-testid="hero-float-pi" className="hero-float flex top-4 left-[3%] md:top-8 md:left-[4%] h-11 w-11 md:h-14 md:w-14 bg-amber-100 text-amber-600 font-extrabold text-xl md:text-2xl ring-4 ring-white" style={{ animation: 'float1 7s ease-in-out infinite' }}>π</div>
          <div data-testid="hero-float-book" className="hero-float flex top-4 right-[4%] md:top-10 md:right-[3%] h-10 w-10 md:h-12 md:w-12 !rounded-2xl bg-pink-100 text-pink-500 ring-4 ring-white" style={{ animation: 'float2 8s ease-in-out infinite' }}>
            <BookOpen className="h-5 w-5 md:h-6 md:w-6" />
          </div>
          <div data-testid="hero-float-math" className="hero-float flex bottom-6 left-[3%] md:bottom-10 md:left-[45%] h-11 w-11 md:h-14 md:w-14 bg-violet-100 text-violet-600 font-extrabold text-base md:text-lg ring-4 ring-white" style={{ animation: 'float3 9s ease-in-out infinite' }}>+×</div>
          <div data-testid="hero-float-sparkle" className="hero-float flex bottom-6 right-[4%] md:top-[48%] md:right-[2%] h-10 w-10 md:h-12 md:w-12 ring-4 ring-white" style={{ background: '#d7ecf4', color: '#2d7d9a', animation: 'float2 10s ease-in-out infinite' }}>
            <Sparkles className="h-5 w-5 md:h-6 md:w-6" />
          </div>
          <div data-testid="hero-float-pen" className="hero-float hidden lg:flex top-[42%] left-[47%] h-12 w-12 bg-emerald-100 text-emerald-600 ring-4 ring-white" style={{ animation: 'float1 8.5s ease-in-out infinite 1s' }}>
            <PenTool className="h-6 w-6" />
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14 md:py-20 grid lg:grid-cols-[1.05fr_1fr] gap-10 items-center">
          {/* LEFT */}
          <div className="fade-up">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight" style={{ color: '#1a3a5c' }}>
              CHECKST<br />
              <Typewriter
                words={['NICHT?', 'MATHE?', 'ENGLISCH?', 'PHYSIK?']}
                speed={110}
                pause={1600}
                deleteSpeed={55}
              />
            </h1>

            <div className="mt-4 mb-8 relative">
              <span className="brush-stroke text-2xl md:text-3xl">WIR HELFEN DIR!</span>
            </div>

            <p className="text-xl md:text-2xl font-bold leading-snug max-w-md" style={{ color: '#1a3a5c' }}>
              <span style={{ color: '#3d94b8' }}>Online-Nachhilfe</span> in allen Fächern – für alle Klassenstufen.
            </p>

            {/* Live availability */}
            <div className="mt-6">
              <LiveAvailability />
            </div>

            {/* Feature list */}
            <div className="mt-9 space-y-5 max-w-md">
              {flyerStudentFeatures.map((f, i) => (
                <WatercolorFeature key={f.title} item={f} delay={i * 90} />
              ))}
            </div>

            {/* CTAs */}
            <Reveal variant="up" delay={100}>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/buchen">
                  <Button className="btn-teal rounded-full px-6 h-12 text-base font-semibold">
                    Jetzt kostenlos beraten lassen
                  </Button>
                </Link>
                <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer">
                  <Button className="btn-whatsapp rounded-full px-6 h-12 text-base font-semibold">
                    <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp
                  </Button>
                </a>
                <Link to="/quiz">
                  <Button variant="outline" className="btn-outline-teal rounded-full px-6 h-12 text-base font-semibold">
                    Bedarfsanalyse starten
                  </Button>
                </Link>
              </div>
            </Reveal>

            {/* Animated stats */}
            <Reveal variant="up" delay={200}>
              <div className="mt-7 grid grid-cols-3 gap-4 max-w-md">
                <div className="text-center">
                  <div className="text-3xl font-extrabold" style={{ color: '#1a3a5c' }}>
                    <CountUpStat end={4.9} decimals={1} suffix="/5" />
                  </div>
                  <div className="text-xs text-slate-500 font-semibold mt-0.5">Bewertung ⭐</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-extrabold" style={{ color: '#1a3a5c' }}>
                    <CountUpStat end={120} suffix="+" />
                  </div>
                  <div className="text-xs text-slate-500 font-semibold mt-0.5">Schüler</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-extrabold" style={{ color: '#1a3a5c' }}>
                    <CountUpStat end={95} suffix="%" />
                  </div>
                  <div className="text-xs text-slate-500 font-semibold mt-0.5">Erfolgsquote</div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* RIGHT */}
          <Reveal variant="right" delay={200}>
            <div className="relative">
              <div className="relative">
                <img
                  src={HERO_IMG}
                  alt="Schüler bei der Online-Nachhilfe"
                  className="w-full h-[500px] md:h-[600px] object-cover"
                  style={{
                    WebkitMaskImage: 'linear-gradient(to top right, transparent 0%, rgba(0,0,0,0.15) 8%, black 32%)',
                    maskImage: 'linear-gradient(to top right, transparent 0%, rgba(0,0,0,0.15) 8%, black 32%)',
                    filter: 'drop-shadow(0 30px 40px rgba(26,58,92,0.10))',
                    animation: 'floatSlow 8s ease-in-out infinite',
                  }}
                  loading="eager"
                />
                <div className="absolute top-6 left-10 bg-white rounded-full shadow-lg px-4 py-2 flex items-center gap-2 border border-slate-100">
                  <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ background: '#e6f2f7' }}>
                    <User className="h-4 w-4" style={{ color: '#3d94b8' }} />
                  </div>
                  <span className="text-sm font-bold" style={{ color: '#1a3a5c' }}>Ab Klasse 1 – Uni</span>
                </div>
              </div>

              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="mt-6 block rounded-[1.5rem] p-5 border-2 shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5"
                style={{ background: '#3d94b8', borderColor: '#2d7d9a' }}
              >
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 h-14 w-14 rounded-full bg-white flex items-center justify-center shadow-md">
                    <MessageCircle className="h-8 w-8" style={{ color: '#25d366' }} />
                  </div>
                  <div className="flex-1">
                    <div className="text-white font-extrabold text-lg leading-tight">Schreib uns auf WhatsApp!</div>
                    <div className="text-white/80 text-sm mt-0.5">Antwort in ca. 15 Minuten</div>
                  </div>
                  <span className="hidden sm:inline-flex items-center gap-2 bg-white font-bold text-sm rounded-full px-5 py-2.5 shadow" style={{ color: '#1a3a5c' }}>
                    Jetzt anfragen <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VIRTUELLER KLASSENRAUM */}
      <section className="py-20 md:py-24" style={{ background: 'linear-gradient(180deg, #f5f9fb 0%, #ffffff 100%)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <div className="relative">
              <img
                src={ONLINE_IMG}
                alt="Virtueller Klassenraum"
                className="w-full h-[460px] object-cover rounded-[2rem] shadow-xl"
              />
              {/* Floating badge: Live-Kamera */}
              <div className="absolute top-5 left-5 bg-white rounded-2xl shadow-lg px-3.5 py-2 flex items-center gap-2 border border-slate-100">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping" style={{ background: '#ef4444' }} />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: '#ef4444' }} />
                </span>
                <span className="text-xs font-bold" style={{ color: '#1a3a5c' }}>Live-Session</span>
              </div>
              {/* Floating badge: 24/7 */}
              <div className="absolute bottom-5 right-5 bg-white rounded-2xl shadow-lg px-3.5 py-2 flex items-center gap-2 border border-slate-100">
                <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ background: '#e6f2f7' }}>
                  <Clock3 className="h-4 w-4" style={{ color: '#3d94b8' }} />
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Zugriff</div>
                  <div className="text-xs font-bold" style={{ color: '#1a3a5c' }}>24/7 verfügbar</div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal variant="right">
            <div>
              <span className="inline-flex items-center gap-2 font-semibold uppercase tracking-wider text-xs" style={{ color: '#3d94b8' }}>
                <Video className="h-4 w-4" /> Online
              </span>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold leading-tight" style={{ color: '#1a3a5c' }}>
                Dein virtueller <span style={{ color: '#3d94b8' }}>Klassenraum</span> – immer bei dir
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Unser digitaler Klassenraum mit HD-Video, interaktivem Whiteboard und allen Materialien –
                jederzeit erreichbar, auch nach der Stunde. Ein Login, ein Klick, los geht's.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Clock,     title: 'Flexibel & jederzeit', text: 'Termine, die zu deinem Alltag passen – auch abends und am Wochenende.' },
                  { icon: HomeIcon,  title: 'Bequem von Zuhause',   text: 'Kein Anfahrtsweg, keine Wartezeit. Einfach Laptop auf und los.' },
                  { icon: PenTool,   title: 'Interaktives Whiteboard', text: 'Gemeinsam schreiben, zeichnen und Aufgaben lösen wie im Klassenzimmer.' },
                  { icon: Cloud,     title: 'Alles gespeichert',    text: 'Notizen und Materialien digital – immer griffbereit für Wiederholung.' },
                ].map((b, i) => (
                  <Reveal key={b.title} variant="up" delay={i * 80}>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 h-11 w-11 rounded-2xl flex items-center justify-center" style={{ background: '#e6f2f7' }}>
                        <b.icon className="h-5 w-5" style={{ color: '#3d94b8' }} />
                      </div>
                      <div>
                        <h4 className="font-bold text-sm" style={{ color: '#1a3a5c' }}>{b.title}</h4>
                        <p className="text-xs text-slate-600 mt-1 leading-relaxed">{b.text}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/buchen">
                  <Button className="btn-teal rounded-full h-12 px-6">
                    <Video className="mr-2 h-4 w-4" /> Kostenlose Probestunde
                  </Button>
                </Link>
                <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="btn-outline-teal rounded-full h-12 px-6">
                    Kurz fragen auf WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROBLEMS vs APPROACH */}
      <section id="ansatz" className="py-20 md:py-24 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <span className="font-semibold uppercase tracking-wider text-xs" style={{ color: '#3d94b8' }}>Unser Ansatz</span>
              <h2 className="mt-2 text-3xl md:text-4xl font-extrabold" style={{ color: '#1a3a5c' }}>Kommt Ihnen das bekannt vor?</h2>
              <p className="mt-4 text-slate-600">
                Häufig sind es nicht „zu wenig lernen" – sondern fehlende Struktur, falsche Methode oder Unsicherheit.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid lg:grid-cols-2 gap-6">
            <Reveal variant="left">
              <div className="relative rounded-[1.8rem] p-7 md:p-9 bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-100 shadow-sm h-full">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl bg-rose-100 border border-rose-200 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-rose-500" />
                  </div>
                  <h3 className="text-xl font-extrabold" style={{ color: '#1a3a5c' }}>Typische Probleme</h3>
                </div>
                <ul className="mt-6 space-y-3">
                  {painProblems.map((p) => (
                    <li key={p} className="flex items-center gap-3 bg-white/70 backdrop-blur-sm rounded-xl px-4 py-3 border border-rose-100/60 transition-transform hover:translate-x-1">
                      <span className="flex-shrink-0 h-7 w-7 rounded-full bg-rose-100 flex items-center justify-center">
                        <X className="h-4 w-4 text-rose-500" />
                      </span>
                      <span className="text-slate-700 text-sm font-medium">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal variant="right">
              <div className="relative rounded-[1.8rem] p-7 md:p-9 border shadow-sm h-full" style={{ background: 'linear-gradient(135deg, #f0f7fa 0%, #e6f2f7 100%)', borderColor: '#b3d9e6' }}>
                <div className="absolute top-6 right-6">
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-white px-2.5 py-1 rounded-full shadow-sm" style={{ background: '#3d94b8' }}>
                    Unser Ansatz
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl flex items-center justify-center border" style={{ background: '#b3d9e6', borderColor: '#3d94b8' }}>
                    <CheckCircle2 className="h-6 w-6" style={{ color: '#2d7d9a' }} />
                  </div>
                  <h3 className="text-xl font-extrabold" style={{ color: '#1a3a5c' }}>So machen wir's anders</h3>
                </div>
                <p className="mt-5 text-slate-700 leading-relaxed">
                  <strong>AvantiTutor</strong> arbeitet nicht nur am Stoff – sondern an
                  {' '}<span className="px-1.5 py-0.5 rounded font-semibold" style={{ background: '#b3d9e6', color: '#1a3a5c' }}>Struktur</span>,
                  {' '}<span className="px-1.5 py-0.5 rounded font-semibold" style={{ background: '#b3d9e6', color: '#1a3a5c' }}>Verständnis</span> und
                  {' '}<span className="px-1.5 py-0.5 rounded font-semibold" style={{ background: '#b3d9e6', color: '#1a3a5c' }}>Selbstvertrauen</span>.
                </p>
                <ul className="mt-5 space-y-3">
                  {ourSolutions.map((s) => (
                    <li key={s} className="flex items-center gap-3 bg-white/70 backdrop-blur-sm rounded-xl px-4 py-3 border border-slate-100 transition-transform hover:-translate-x-1">
                      <span className="flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center" style={{ background: '#b3d9e6' }}>
                        <Check className="h-4 w-4" style={{ color: '#2d7d9a' }} />
                      </span>
                      <span className="text-slate-700 text-sm font-medium">{s}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/buchen" className="mt-7 inline-flex items-center gap-2 font-semibold hover:gap-3 transition-all" style={{ color: '#2d7d9a' }}>
                  Kostenlose Bedarfsanalyse starten <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* IN 4 SCHRITTEN */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <img src={ABOUT_IMG} alt="Strukturierte Nachhilfe" className="w-full h-[460px] object-cover rounded-[2rem] shadow-xl" />
          </Reveal>
          <Reveal variant="right">
            <div>
              <span className="inline-block font-semibold uppercase tracking-wider text-xs" style={{ color: '#3d94b8' }}>In 4 Schritten</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold leading-tight" style={{ color: '#1a3a5c' }}>So bringen wir Schüler messbar nach vorne</h2>
              <p className="mt-4 text-slate-600">Vier klare Schritte – keine „Wunderversprechen", sondern solides Handwerk.</p>
              <div className="mt-8 space-y-5">
                {approachSteps.map((s, i) => (
                  <Reveal key={s.step} variant="up" delay={i * 100}>
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 h-11 w-11 rounded-xl bg-white border shadow-sm flex items-center justify-center font-bold" style={{ borderColor: '#b3d9e6', color: '#2d7d9a' }}>
                        {s.step}
                      </div>
                      <div>
                        <h4 className="font-bold" style={{ color: '#1a3a5c' }}>{s.title}</h4>
                        <p className="text-slate-600 text-sm mt-1">{s.text}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Link to="/buchen"><Button className="btn-teal rounded-full mt-8 px-6">Kostenloses Erstgespräch</Button></Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* UNSERE WERTE */}
      <section className="py-20 md:py-24" style={{ background: 'linear-gradient(180deg, #f5f9fb 0%, #ffffff 100%)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="font-semibold uppercase tracking-wider text-xs" style={{ color: '#3d94b8' }}>Unsere Werte</span>
              <h2 className="mt-2 text-3xl md:text-4xl font-extrabold" style={{ color: '#1a3a5c' }}>Was uns antreibt</h2>
              <p className="mt-4 text-slate-600">Was uns von klassischen Nachhilfe-Instituten unterscheidet.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => {
              const IconComp = VALUE_ICONS[v.icon] || Sparkles;
              return (
                <Reveal key={v.title} variant="up" delay={i * 90}>
                  <TiltCard>
                    <div className="card-soft p-6 text-center h-full">
                      <div className="h-14 w-14 mx-auto rounded-2xl flex items-center justify-center text-white shadow-md" style={{ background: 'linear-gradient(135deg, #4aa5c8, #2d7d9a)' }}>
                        <IconComp className="h-7 w-7" />
                      </div>
                      <h3 className="mt-4 font-bold" style={{ color: '#1a3a5c' }}>{v.title}</h3>
                      <p className="mt-2 text-sm text-slate-600">{v.text}</p>
                    </div>
                  </TiltCard>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PREISE / PREISRECHNER */}
      <section id="preise" className="py-20 md:py-24 scroll-mt-24" style={{ background: 'linear-gradient(180deg, #f5f9fb 0%, #ffffff 100%)' }}>
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="text-center mb-10">
              <span className="font-semibold uppercase tracking-wider text-xs" style={{ color: '#3d94b8' }}>Preise</span>
              <h2 className="mt-2 text-3xl md:text-4xl font-extrabold" style={{ color: '#1a3a5c' }}>Faire Preise, keine Vertragsbindung</h2>
              <p className="mt-4 text-slate-600 max-w-2xl mx-auto">Transparent nach Stufe, Fach und Dauer – berechne deinen Monatspreis in Sekunden.</p>
            </div>
          </Reveal>
          <PriceCalculator />
        </div>
      </section>

      {/* SUBJECTS with 3D tilt */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
              <div>
                <span className="font-semibold uppercase tracking-wider text-xs" style={{ color: '#3d94b8' }}>Fächer</span>
                <h2 className="mt-2 text-3xl md:text-4xl font-extrabold" style={{ color: '#1a3a5c' }}>Diese Fächer decken wir ab</h2>
              </div>
              <Link to="/faecher" className="font-semibold hover:underline inline-flex items-center gap-1" style={{ color: '#2d7d9a' }}>Alle Fächer <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {subjects.slice(0, 6).map((s, i) => (
              <Reveal key={s.name} variant="up" delay={i * 60}>
                <TiltCard>
                  <Link to="/faecher" className="card-soft p-5 text-center group block">
                    <div className={`h-12 w-12 mx-auto rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center text-white shadow-md group-hover:scale-110 transition-transform`}>
                      <Icon name={s.icon} className="h-6 w-6" />
                    </div>
                    <div className="mt-3 font-semibold text-sm" style={{ color: '#1a3a5c' }}>{s.name}</div>
                    <div className="text-xs text-slate-500 mt-1">{s.levels}</div>
                  </Link>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS - handled by separate component */}
      <TestimonialsCarousel />

      {/* CTA */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <Reveal variant="scale">
            <div className="relative rounded-[2rem] overflow-hidden p-10 md:p-14 text-white shadow-2xl" style={{ background: 'linear-gradient(135deg, #2d7d9a 0%, #3d94b8 100%)' }}>
              <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
              <div className="absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
              <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
                <div>
                  <h3 className="text-2xl md:text-3xl font-extrabold">Bereit für bessere Noten?</h3>
                  <p className="mt-2 text-white/90">Starten Sie mit einer kostenlosen Bedarfsanalyse – unverbindlich, ehrlich, direkt.</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link to="/buchen"><Button className="bg-white hover:bg-slate-50 rounded-full h-12 px-6 font-semibold" style={{ color: '#2d7d9a' }}>Termin buchen</Button></Link>
                  <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer"><Button className="btn-whatsapp rounded-full h-12 px-6 font-semibold"><MessageCircle className="mr-2 h-5 w-5" /> WhatsApp</Button></a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

/* Testimonials Carousel with auto-play + hover pause */
function TestimonialsCarousel() {
  const [i, setI] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const perView = 3;
  const total = testimonials.length;

  React.useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setI((p) => (p + 1) % total), 4500);
    return () => clearInterval(id);
  }, [paused, total]);

  const visible = Array.from({ length: perView }).map((_, k) => testimonials[(i + k) % total]);

  return (
    <section className="py-20 md:py-24" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f5f9fb 100%)' }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-semibold uppercase tracking-wider text-xs" style={{ color: '#3d94b8' }}>Erfahrungsberichte</span>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold" style={{ color: '#1a3a5c' }}>Was unsere Schüler & Eltern sagen</h2>
          </div>
        </Reveal>

        <div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {visible.map((t, k) => (
            <div key={`${t.name}-${i}-${k}`} className="card-soft p-6" style={{ animation: 'fadeUp 500ms ease both' }}>
              <div className="flex gap-0.5 text-amber-400">
                {Array.from({ length: t.rating }, (_, x) => <Star key={`${t.name}-star-${x}`} className="h-4 w-4 fill-amber-400" />)}
              </div>
              <p className="mt-4 text-slate-700 text-sm leading-relaxed">„{t.text}"</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full text-white flex items-center justify-center font-bold text-sm" style={{ background: 'linear-gradient(135deg, #4aa5c8, #2d7d9a)' }}>{t.initials}</div>
                <div>
                  <div className="font-semibold text-sm" style={{ color: '#1a3a5c' }}>{t.name}</div>
                  <div className="text-xs text-slate-500">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Dots */}
        <div className="mt-8 flex justify-center gap-2">
          {testimonials.map((t, k) => (
            <button
              key={t.name}
              onClick={() => setI(k)}
              aria-label={`Erfahrungsbericht ${k + 1}`}
              className="h-2 rounded-full transition-all"
              style={{
                width: k === i ? 24 : 8,
                background: k === i ? '#3d94b8' : '#cbd5e1',
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
